/**
 * Cambium Networks radio-antenna catalog.
 *
 * Cambium is the carrier-class incumbent that ePMP/PMP installers
 * reach for when Ubiquiti's airMAX doesn't have enough SLA. ePMP
 * Force runs 5 GHz 802.11ac with proprietary scheduling extensions
 * (better than vanilla 802.11 in dense PtMP deployments); PMP 450
 * runs Cambium's own MAC (Canopy MAC) and is the licensed-WISP
 * standard.
 *
 * All entries verified against cambiumnetworks.com product pages
 * and the official datasheet PDFs hosted under
 * cambiumnetworks.com/wp-content/uploads/.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const CAMBIUM_RADIO_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // ePMP — 5 GHz 802.11ac CPE / PtP
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cambiumnetworks.com/products/epmp/force-300-25/
  // SRC: https://www.daywireless.com/downloads/cambium-networks/cambium-networks-epmp-force300-25-specsheet.pdf
  // SRC: https://www.cambiumnetworks.com/wp-content/uploads/2020/11/Cambium_Networks_data_sheet_ePMP_Force_300_Series.pdf
  //      ePMP Force 300-25: 5 GHz 802.11ac, integrated 25 dBi dish with
  //      narrow beamwidth, up to 600 Mbps peak / 500+ Mbps real user
  //      data, wide-band 4910-5970 MHz (country regs apply), 20-80 MHz
  //      channel widths, 256-QAM modulation. PtP or as PtMP SM with
  //      ePMP 3000 / 3000L. NOTE: 25 dBi dish suggests beam-width
  //      around 7° azimuth (typical for that gain at 5 GHz).
  {
    id: 'cambium-epmp-force-300-25',
    manufacturer: 'Cambium',
    model: 'ePMP Force 300-25',
    category: 'radio-antenna',
    tagline:
      'ePMP Force 300-25 — 5 GHz 802.11ac, 25 dBi dish, 600 Mbps peak, PtP/PtMP',
    verified: true,
    datasheetUrl: 'https://www.cambiumnetworks.com/products/epmp/force-300-25/',
    specs: {
      band: '5 GHz',
      rangeM: 25000, // 25 dBi @ 5 GHz typically delivers 25-40 km LoS
      gainDbi: 25,
      beamWidthDeg: 7, // typical for 25 dBi at 5 GHz
      throughputMbps: 600,
      standard: '802.11ac',
      mimo: '2×2',
      ipRating: 'IP55',
      operatingTempC: '-30..+60',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PMP 450 — carrier-class licensed-WISP CPE
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cambiumnetworks.com/wp-content/uploads/2019/01/CN_data_sheet_PMP_450i_SM.pdf
  // SRC: https://www.cambiumnetworks.com/products/pmp-450/
  // SRC: https://www.balticnetworks.com/products/cambium-5-ghz-pmp-450i-sm-integrated-high-gain-antenna
  //      PMP 450i SM (Subscriber Module, 5 GHz): 4900-5925 MHz, 2×2
  //      OFDM MIMO, up to 300 Mbps aggregate in 40 MHz channel,
  //      channel widths 5/7/10/15/20/30/40 MHz, max deployment range
  //      up to 64 km (40 miles), IP66/IP67 outdoor rated. Available
  //      in connectorized (C050045C001B) and integrated (C050045C002C)
  //      versions. Integrated antenna 23 dBi.
  {
    id: 'cambium-pmp-450i-sm-integrated',
    manufacturer: 'Cambium',
    model: 'PMP 450i SM (Integrated)',
    category: 'radio-antenna',
    tagline:
      'PMP 450i SM — 5 GHz carrier-WISP CPE, 23 dBi integrated, 300 Mbps, 64 km',
    verified: true,
    datasheetUrl:
      'https://www.cambiumnetworks.com/wp-content/uploads/2019/01/CN_data_sheet_PMP_450i_SM.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 64000, // up to 64 km / 40 miles per datasheet
      gainDbi: 23,
      beamWidthDeg: 9, // typical for 23 dBi integrated dish at 5 GHz
      throughputMbps: 300,
      standard: 'proprietary', // Canopy MAC, not vanilla 802.11
      mimo: '2×2',
      ipRating: 'IP66',
      operatingTempC: '-40..+60',
    },
  },

  // SRC: https://www.cambiumnetworks.com/products/pmp-distribution/5-ghz-pmp-450b-high-gain-subscriber-module/
  // SRC: https://www.cambiumnetworks.com/resource/5-ghz-pmp-450b-high-gain-sm-data-sheet/
  // SRC: https://www.daywireless.com/downloads/cambium-networks/cambium-networks-pmp-450b-specsheet.pdf
  //      PMP 450b High Gain SM: 5 GHz 4.9-5.925 GHz, integrated 24 dBi
  //      antenna, 300 Mbps aggregate in 40 MHz channel. Lower-cost
  //      449 SM successor to 450i for high-density deployments;
  //      retains Canopy MAC + sync compatibility with PMP 450 sectors.
  {
    id: 'cambium-pmp-450b-high-gain',
    manufacturer: 'Cambium',
    model: 'PMP 450b High Gain SM',
    category: 'radio-antenna',
    tagline:
      'PMP 450b High Gain SM — 5 GHz, 24 dBi integrated, 300 Mbps, 40 MHz channels',
    verified: true,
    datasheetUrl:
      'https://www.cambiumnetworks.com/resource/5-ghz-pmp-450b-high-gain-sm-data-sheet/',
    specs: {
      band: '5 GHz',
      rangeM: 30000, // typical 25-35 km LoS for 24 dBi @ 5 GHz
      gainDbi: 24,
      beamWidthDeg: 8, // typical for 24 dBi at 5 GHz
      throughputMbps: 300,
      standard: 'proprietary', // Canopy MAC
      mimo: '2×2',
      ipRating: 'IP66',
      operatingTempC: '-40..+60',
    },
  },
];
